import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { card, Btn, Badge, Modal, Field, Select, Input, Textarea, inputCls } from "./ui";
import { Trash2, Send } from "lucide-react";

const STATUSES = ["ALL", "NEW", "CONTACTED", "FOLLOW-UP", "QUOTED", "NEGOTIATION", "CONFIRMED", "COMPLETED", "CANCELLED"];
const CATEGORIES = ["ALL", "EVENT ENQUIRY", "WEDDING ENQUIRY", "CORPORATE FOOD ENQUIRY", "BULK FOOD ENQUIRY", "MENU ENQUIRY", "PRIVATE EVENT", "GENERAL ENQUIRY", "DAILY FOOD SERVICE"];
const tone = { NEW: "gold", CONFIRMED: "green", COMPLETED: "green", CANCELLED: "red" };

export default function Enquiries() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("ALL");
  const [cat, setCat] = useState("ALL");
  const [sel, setSel] = useState(null);
  const [note, setNote] = useState("");
  const [staff, setStaff] = useState([]);

  const load = useCallback(() => {
    api.get(`/admin/enquiries?status=${status}&category=${cat}`).then(({ data }) => setRows(data)).catch(() => {});
  }, [status, cat]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/admin/users").then(({ data }) => setStaff(data)).catch(() => setStaff([])); }, []);

  const patch = async (id, body) => {
    const { data } = await api.patch(`/admin/enquiries/${id}`, body);
    setRows((r) => r.map((x) => (x.id === id ? data : x)));
    if (sel?.id === id) setSel(data);
  };
  const addNote = async () => {
    if (!note.trim()) return;
    await api.post(`/admin/enquiries/${sel.id}/notes`, { text: note });
    const { data } = await api.get(`/admin/enquiries/${sel.id}`);
    setSel(data); setNote("");
  };
  const del = async (id) => { if (!window.confirm("Delete this enquiry?")) return; await api.delete(`/admin/enquiries/${id}`); setRows((r) => r.filter((x) => x.id !== id)); setSel(null); };

  return (
    <div data-testid="admin-enquiries">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div><p className="text-gold/70 text-xs tracking-[0.25em] uppercase">Enquiries</p><h1 className="font-serif text-4xl text-ivory mt-1">Enquiry Inbox</h1></div>
        <div className="flex gap-3">
          <div className="w-40"><Select value={status} onChange={(e) => setStatus(e.target.value)} options={STATUSES} /></div>
          <div className="w-52"><Select value={cat} onChange={(e) => setCat(e.target.value)} options={CATEGORIES} /></div>
        </div>
      </div>

      <div className={`${card} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-ivory/40 text-left text-xs uppercase tracking-wider border-b border-white/5">
              <th className="p-3.5">Name</th><th className="p-3.5">Event</th><th className="p-3.5 hidden md:table-cell">Category</th><th className="p-3.5 hidden sm:table-cell">Guests</th><th className="p-3.5 hidden lg:table-cell">Date</th><th className="p-3.5">Status</th>
            </tr></thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.id} onClick={() => setSel(e)} data-testid={`enquiry-row-${e.id}`} className="border-b border-white/5 hover:bg-white/5 cursor-pointer">
                  <td className="p-3.5 text-ivory">{e.name}<div className="text-ivory/40 text-xs">{e.phone}</div></td>
                  <td className="p-3.5 text-ivory/70">{e.event_type || "—"}</td>
                  <td className="p-3.5 hidden md:table-cell"><Badge tone="gray">{(e.category || "").replace(" ENQUIRY", "")}</Badge></td>
                  <td className="p-3.5 text-ivory/70 hidden sm:table-cell">{e.guests || "—"}</td>
                  <td className="p-3.5 text-ivory/60 hidden lg:table-cell">{e.event_date || "—"}</td>
                  <td className="p-3.5"><Badge tone={tone[e.status] || "blue"}>{e.status}</Badge></td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={6} className="p-10 text-center text-ivory/40">No enquiries found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal open={!!sel} onClose={() => setSel(null)} title={sel?.name} wide>
        {sel && (
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {[["Phone", sel.phone], ["Email", sel.email], ["Event", sel.event_type], ["Date", sel.event_date], ["Guests", sel.guests], ["Location", sel.location], ["Cuisine", sel.cuisine], ["Meal", sel.meal_preference], ["Service", sel.service_type], ["Budget", sel.budget], ["Source", sel.source], ["Menu Ref", sel.menu_ref]].map(([k, v]) => (
              <div key={k}><span className="text-ivory/40 text-xs uppercase tracking-wide">{k}</span><p className="text-ivory">{v || "—"}</p></div>
            ))}
            <div className="sm:col-span-2"><span className="text-ivory/40 text-xs uppercase tracking-wide">Message</span><p className="text-ivory/80">{sel.message || "—"}</p></div>

            <div className="sm:col-span-2 grid sm:grid-cols-3 gap-3 mt-3 pt-4 border-t border-white/10">
              <Field label="Status"><Select value={sel.status} onChange={(e) => patch(sel.id, { status: e.target.value })} options={STATUSES.slice(1)} /></Field>
              <Field label="Category"><Select value={sel.category} onChange={(e) => patch(sel.id, { category: e.target.value })} options={CATEGORIES.slice(1)} /></Field>
              <Field label="Assign To"><Select value={sel.assigned_to || ""} onChange={(e) => patch(sel.id, { assigned_to: e.target.value })} options={[{ value: "", label: "Unassigned" }, ...staff.map((u) => ({ value: u.id, label: u.name }))]} /></Field>
            </div>

            <div className="sm:col-span-2">
              <span className="text-ivory/40 text-xs uppercase tracking-wide">Internal Notes</span>
              <div className="space-y-2 my-2 max-h-40 overflow-y-auto dark-scroll">
                {(sel.notes || []).map((n) => <div key={n.id} className="bg-[#1C1210] rounded-lg p-2.5"><p className="text-ivory/80 text-sm">{n.text}</p><p className="text-ivory/30 text-xs mt-1">{n.author}</p></div>)}
                {!(sel.notes || []).length && <p className="text-ivory/30 text-sm">No notes yet.</p>}
              </div>
              <div className="flex gap-2">
                <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className={inputCls} data-testid="enquiry-note-input" />
                <Btn onClick={addNote} data-testid="enquiry-note-add"><Send className="w-4 h-4" /></Btn>
              </div>
            </div>
            <div className="sm:col-span-2 flex justify-between pt-2">
              <Btn variant="danger" onClick={() => del(sel.id)}><Trash2 className="w-4 h-4" /> Delete</Btn>
              <a href={`tel:${sel.phone}`} className="text-gold text-sm self-center">Call {sel.phone}</a>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
